import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Alert,
  MenuItem,
  Grid,
  Divider,
  CircularProgress,
  Snackbar,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import { REPS_COLORS } from '../../utils/constants';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ReplayIcon from '@mui/icons-material/Replay';
import { useDemoRole } from '../../../../contexts/DemoRoleContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUserOrganizations } from '../../hooks/useOrganization';
import { CONDITION_KINDS, TRL_TYPES, DRIFT_TYPES, TRAP_CATEGORIES, SEVERITY_LEVELS, conditionStatusLabel, conditionStatusColor, conditionTypeLabel, conditionSubtypeLabel, resolvedSubtitle, HIDDEN_FROM_QUEUES } from '../../utils/trlConstants';
import { siteCodeFor } from '../../utils/conditionIds';
import { createTRL, updateTRLBySubmitter, withdrawTRL, submitResolution, reopenTRL, hideConditionFor, getTRL } from '../../../../services/trlService';
import { useConditionViewer } from '../../hooks/useConditionViewer';
import { canResolve, canHide, isSingleStep, PERSONA_TO_ROLE, routingFor } from '../../utils/conditionWorkflow';
import { useNavigate } from 'react-router-dom';
import { ConditionDetailDrawer } from '../../components/conditions/ConditionDetailDrawer';
import { notifyConditionEvent } from '../../../../services/conditionNotificationService';
import { useSearchParams } from 'react-router-dom';
import { triggerTrlAnalysis } from '../../../../services/n8nService';
import { useTRLs } from '../../hooks/useTRLs';
import { formatTimestamp } from '../../../../utils/firestore';

const REPS_QUADRANTS = [
  { value: 'reliability', label: 'Reliability' },
  { value: 'efficiency', label: 'Efficiency' },
  { value: 'productivity', label: 'Productivity' },
  { value: 'safety', label: 'Safety' },
];

// Labels + chip colors come from trlConstants (Tim's status flags, D06 §5.1; P2-05).
const statusMetaOf = (status) => ({ label: conditionStatusLabel(status), color: conditionStatusColor(status) });

const typeLabelOf = (t) => conditionTypeLabel(t) || t;
const sevMetaOf = (v) => SEVERITY_LEVELS.find((s) => s.value === v);

/**
 * TRL Entry — Mindmap branch 2, "Individual (ITM) — Condition Entry → Assign Severity Level"
 *
 * Form for an ITM to submit a Trap, Roadblock, or Landmine and propose a severity.
 */

/** Numbered step header — the three visual steps of the entry form (What · Where · How bad). */
function StepLabel({ n, text }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'brand.band', color: 'brand.onBand', fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</Box>
      <Typography variant="subtitle1" fontWeight={700}>{text}</Typography>
    </Box>
  );
}

function TRLEntry() {
  const { personaUser, queryScope } = useDemoRole();
  const viewer = useConditionViewer();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reviewTarget, setReviewTarget] = useState(null);
  const [type, setType] = useState(''); // '' until the entrant chooses (Tim 26 Oct: nothing pre-selected)
  const [kind, setKind] = useState(''); // 'drift' | 'trl' — the first choice; `type` is the stored value
  const [driftType, setDriftType] = useState('');
  const [trapCategory, setTrapCategory] = useState('');
  const [ownerKey, setOwnerKey] = useState(''); // 'dept:<id>' | 'nwt:<id>' — recommended owner (P2-04)
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('II');
  const [quadrants, setQuadrants] = useState([]); // Tim 20 Oct: nothing pre-checked
  const toggleQuad = (v) => setQuadrants((qs) => (qs.includes(v) ? qs.filter((x) => x !== v) : [...qs, v]));
  // Default the NWT to the persona's NWT if they have one (ITM/FLS), else first available
  const [nwtId, setNwtId] = useState(queryScope?.nwtId || '');
  const [showOwner, setShowOwner] = useState(false);
  const personaNwt = queryScope?.nwtId || null;
  // P2-24: the NWT is always selectable (Tim tests as admin for Crew Charlie); the person's own
  // team is preselected. `nwtLocked` stays as a hint only.
  const [nwtLocked, setNwtLocked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [lastTrlId, setLastTrlId] = useState(null);
  const [lastConditionId, setLastConditionId] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('OE Condition submitted — routed for review. DREW is analyzing it now.');
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rowBusy, setRowBusy] = useState(false);
  const [fixTarget, setFixTarget] = useState(null);
  const [fixNote, setFixNote] = useState('');

  const isEditing = Boolean(editingId);


  // Live list of the submitter's own TRLs (persona-scoped). Persists after the
  // toast fades so the user always has proof of what they logged — and the AI
  // suggestion appears on a row live once Workflow 4 returns.
  const { trls } = useTRLs({ limit: 50 });
  const myUserId = personaUser?.id || queryScope?.userId;
  const mySubmissions = trls
    .filter((t) => (!myUserId || t.submittedBy === myUserId) && !HIDDEN_FROM_QUEUES.includes(t.status))
    .slice(0, 5);
  // Deep link from the detail drawer / activity feed: ?edit=<id> (Pending Review only) or ?condition=<id> (review view)
  // Tim 26 Oct (D03 r22): a Plant Manager opening a condition logged elsewhere landed on a blank
  // form, because the id was only looked up in the lens-scoped list. Fall back to a direct read.
  useEffect(() => {
    const editId = searchParams.get('edit');
    const viewId = searchParams.get('condition');
    const id = editId || viewId;
    if (!id) return;
    let cancelled = false;
    const open = (target) => {
      if (cancelled || !target) return;
      if (editId && target.status === 'submitted') startEdit(target);
      else setReviewTarget(target);
      setSearchParams({}, { replace: true });
    };
    const local = trls.find((t) => t.id === id);
    if (local) open(local);
    else getTRL(id).then(open).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, trls.length]);

  const lastCondition = lastTrlId ? trls.find((t) => t.id === lastTrlId) : null;
  const shownConditionId = lastCondition?.conditionId || lastConditionId;

  const typeIcon = (t) => {
    if (t === 'drift') return <TrendingDownIcon />;
    if (t === 'trap') return <WarningAmberIcon />;
    if (t === 'roadblock') return <BlockIcon />;
    return <VisibilityOffIcon />;
  };

  // Team picker: the active org's REAL teams only. Teams write real org unit ids.
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const activeOrg = organizations[0] || null;
  const navigate = useNavigate();
  const REVIEWER_LABELS = { team_lead: 'your Frontline Supervisor', department_lead: 'your Department Head', site_manager: 'your Site Manager', org_admin: 'the REPSit administrator' };
  const nextReviewerLabel = REVIEWER_LABELS[routingFor(lastCondition?.submitterRole || PERSONA_TO_ROLE[queryScope?.personaId] || 'respondent', activeOrg?.reviewRouting || null).l1] || 'your reviewer';
  const teamOptions = (activeOrg?.teams || []).map((t) => {
    const dept = (activeOrg.departments || []).find((d) => d.id === t.departmentId);
    const site = dept ? (activeOrg.sites || []).find((s) => s.id === dept.siteId) : null;
    return {
      id: t.id,
      name: `${t.name}${site ? ` — ${site.name}` : ''}`,
      deptId: dept?.id || null,
      siteId: site?.id || null,
      organizationId: activeOrg.id,
    };
  });
  const noTeams = teamOptions.length === 0;
  const missingSubtype = !type || (type === 'drift' && !driftType) || (type === 'trap' && !trapCategory);
  const missingRequired = !title || !description || quadrants.length === 0 || noTeams || !nwtId || missingSubtype;

  // Recommended owner options (P2-04): departments grouped by site, plus NWTs.
  // Tim 26 Oct (D03 r18): ownership can also be recommended at plant/site or executive level — those
  // resolve to the person holding the role (ownerUid), which the resolve rules already honour.
  const orgMembers = viewer?.members || [];
  const execOwners = orgMembers
    .filter((m) => m.role === 'executive' || m.role === 'org_admin')
    .map((m) => ({ key: `uid:${m.uid}`, label: `${m.role === 'executive' ? 'Executive' : 'Administrator'} — ${m.displayName || m.email || m.uid}`, deptId: null, nwtId: null, ownerUid: m.uid, ownerName: m.displayName || m.email || '', group: 'Organization' }));
  const ownerOptions = [
    ...execOwners,
    ...(activeOrg?.sites || []).flatMap((site) => {
      const siteManagers = orgMembers
        .filter((m) => m.role === 'site_manager' && (m.scopeId === site.id || (Array.isArray(m.scopeIds) && m.scopeIds.includes(site.id))))
        .map((m) => ({ key: `uid:${m.uid}`, label: `Site/Plant Manager — ${site.name} (${m.displayName || m.email || m.uid})`, deptId: null, nwtId: null, ownerUid: m.uid, ownerName: m.displayName || m.email || '', group: site.name }));
      const depts = (activeOrg.departments || []).filter((d) => d.siteId === site.id);
      return [
        ...siteManagers,
        ...depts.flatMap((d) => [
          { key: `dept:${d.id}`, label: `${d.name} — ${site.name}`, deptId: d.id, nwtId: null, group: site.name },
          ...(activeOrg.teams || []).filter((t) => t.departmentId === d.id).map((t) => ({ key: `nwt:${t.id}`, label: `↳ ${t.name} (NWT)`, deptId: d.id, nwtId: t.id, group: site.name })),
        ]),
      ];
    }),
  ];
  const ownerOf = (key) => ownerOptions.find((o) => o.key === key) || null;

  // Keep the selected NWT sensible as org data loads: the persona's team if it
  // exists in the picker, else the first real team.
  useEffect(() => {
    if (teamOptions.length === 0) return;
    setNwtId((current) => {
      if (current && teamOptions.some((t) => t.id === current)) return current;
      if (queryScope?.nwtId && teamOptions.some((t) => t.id === queryScope.nwtId)) return queryScope.nwtId;
      return teamOptions[0].id;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrg?.id, queryScope?.nwtId, teamOptions.length]);

  // Resolve dept + site from the chosen team so the TRL is fully scoped at write time
  const resolveScopeForNwt = (chosenNwtId) => {
    const team = teamOptions.find((t) => t.id === chosenNwtId);
    if (team) {
      return { nwtId: team.id, deptId: team.deptId, siteId: team.siteId, organizationId: team.organizationId };
    }
    return { nwtId: chosenNwtId || null, deptId: null, siteId: null, organizationId: activeOrg?.id || null };
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setType('');
    setKind('');
    setSeverity('II');
    setQuadrants([]);
    setNwtId(queryScope?.nwtId || teamOptions[0]?.id || '');
    setNwtLocked(false);
    setDriftType('');
    setTrapCategory('');
    setOwnerKey('');
  };

  const handleSubmit = async () => {
    if (missingRequired || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const scope = resolveScopeForNwt(nwtId);
      const submitterId = personaUser?.id || queryScope?.userId || 'demo_user';

      if (isEditing) {
        const editedId = editingId;
        const owner = ownerOf(ownerKey);
        await updateTRLBySubmitter(
          editedId,
          {
            type,
            driftType: type === 'drift' ? driftType : null,
            trapCategory: type === 'trap' ? trapCategory : null,
            title,
            description,
            repsQuadrant: quadrants[0],
            repsQuadrants: quadrants,
            severityITM: severity,
            ownerDeptId: owner?.deptId || null,
            ownerNwtId: owner?.nwtId || null,
            ownerUid: owner?.ownerUid || null,
            ownerName: owner?.ownerName || '',
            nwtId: scope.nwtId,
            deptId: scope.deptId,
            siteId: scope.siteId,
          },
          { submittedBy: submitterId }
        );
        setEditingId(null);
        resetForm();
        setToastMsg('Submission updated.');
        setToastOpen(true);
        // Re-run AI analysis on the edited content.
        triggerTrlAnalysis(editedId, {
          personaId: queryScope?.personaId,
          nwtId: scope.nwtId,
          deptId: scope.deptId,
          siteId: scope.siteId,
        });
      } else {
        const owner = ownerOf(ownerKey);
        const site = (activeOrg?.sites || []).find((x) => x.id === scope.siteId) || null;
        const trlId = await createTRL({
          organizationId: scope.organizationId || queryScope?.organizationId || null,
          siteId: scope.siteId,
          siteCode: siteCodeFor(site, activeOrg),
          deptId: scope.deptId,
          nwtId: scope.nwtId,
          submittedBy: submitterId,
          submittedByName: personaUser?.name || 'Demo User',
          // Audit: who's physically at the keyboard (differs from submittedBy
          // only when an admin uses "View as" to log on a member's behalf).
          enteredBy: user?.uid || submitterId,
          enteredByName: user?.displayName || user?.email || '',
          actingFor: user?.uid && submitterId !== user.uid ? { uid: submitterId, name: personaUser?.name || '' } : null,
          type,
          driftType: type === 'drift' ? driftType : null,
          trapCategory: type === 'trap' ? trapCategory : null,
          title,
          description,
          repsQuadrant: quadrants[0],
          repsQuadrants: quadrants,
          severityITM: severity,
          ownerDeptId: owner?.deptId || null,
          ownerNwtId: owner?.nwtId || null,
          ownerUid: owner?.ownerUid || null,
          ownerName: owner?.ownerName || '',
          submitterRole: viewer.role || PERSONA_TO_ROLE[queryScope?.personaId] || 'respondent',
        });
        setLastTrlId(trlId);
        setLastConditionId(null); // filled in by the live list once the doc arrives
        notifyConditionEvent('created', { id: trlId, organizationId: scope.organizationId, siteId: scope.siteId, deptId: scope.deptId, nwtId: scope.nwtId, submittedBy: submitterId, submitterRole: viewer.role, title, ownerDeptId: owner?.deptId || null }, viewer, { actedAs: 'logged an OE Condition' });
        setSubmitted(true);
        setTitle('');
        setDescription('');
        setToastMsg('OE Condition submitted — routed for review. DREW is analyzing it now.');
        setToastOpen(true);
        // Fire-and-forget: kick off n8n Workflow 4 (TRL AI Analysis)
        triggerTrlAnalysis(trlId, {
          personaId: queryScope?.personaId,
          nwtId: scope.nwtId,
          deptId: scope.deptId,
          siteId: scope.siteId,
        });
        // Tim 20 Oct: the banner stays until the user chooses what to do next (it used to vanish after 6 s)
      }
      // Success banner / form is at the top; scroll up so it's seen.
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('TRL submit/update failed:', err);
      setError(err.message || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Load a pre-review submission back into the form for editing.
  const startEdit = (t) => {
    setEditingId(t.id);
    setType(t.type || 'trap');
    setKind(t.type === 'drift' ? 'drift' : 'trl');
    setTitle(t.title || '');
    setDescription(t.description || '');
    setSeverity(t.severityITM || 'II');
    setQuadrants(t.repsQuadrants && t.repsQuadrants.length ? t.repsQuadrants : (t.repsQuadrant ? [t.repsQuadrant] : []));
    setNwtId(t.nwtId || nwtId);
    setNwtLocked(false);
    setDriftType(t.driftType || '');
    setTrapCategory(t.trapCategory || '');
    setOwnerKey(t.ownerUid ? `uid:${t.ownerUid}` : t.ownerNwtId ? `nwt:${t.ownerNwtId}` : t.ownerDeptId ? `dept:${t.ownerDeptId}` : '');
    setError(null);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetForm();
    setError(null);
  };

  const handleWithdraw = async () => {
    if (!deleteTarget) return;
    setRowBusy(true);
    setError(null);
    try {
      await withdrawTRL(deleteTarget.id, {
        submittedBy: personaUser?.id || queryScope?.userId,
      });
      if (editingId === deleteTarget.id) cancelEdit();
      setDeleteTarget(null);
      setToastMsg('Submission withdrawn.');
      setToastOpen(true);
    } catch (err) {
      console.error('Withdraw failed:', err);
      setError(err.message || 'Could not withdraw this submission.');
      setDeleteTarget(null);
    } finally {
      setRowBusy(false);
    }
  };

  // "I fixed it" — close the loop for everyone in the chain.
  const handleResolve = async () => {
    if (!fixTarget) return;
    setRowBusy(true);
    setError(null);
    try {
      const next = await submitResolution(fixTarget.id, {
        note: fixNote,
        by: viewer.uid,
        byName: viewer.name,
        actingFor: viewer.actingFor,
        singleStep: isSingleStep(fixTarget, viewer.routing),
      });
      notifyConditionEvent(next === 'resolved' ? 'resolved' : 'resolution_submitted', fixTarget, viewer, { after: { status: next } });
      setFixTarget(null);
      setFixNote('');
      setToastMsg(next === 'resolved' ? 'Resolved — recorded as found and fixed.' : 'Resolution submitted — it goes to L1, then L2, for approval.');
      setToastOpen(true);
    } catch (err) {
      console.error('Resolve failed:', err);
      setError(err.message || 'Could not mark this as fixed.');
      setFixTarget(null);
    } finally {
      setRowBusy(false);
    }
  };

  const handleReopen = async (t) => {
    setError(null);
    try {
      await reopenTRL(t.id, { reopenedBy: personaUser?.id || queryScope?.userId });
      setToastMsg('Reopened.');
      setToastOpen(true);
    } catch (err) {
      setError(err.message || 'Could not reopen.');
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* ⚑ D-UI-05 (UI review 2026-09-21 §7): Tim's title verbatim, one size smaller, and the form
          shaped as three visual steps — What · Where · How bad — "the entry Level one needs to be
          real simple". No data-model change. */}
      <Typography variant="h5" gutterBottom sx={{ textWrap: 'balance' }}>OE Condition Entry: Drift and Accumulation of TRLs (Traps, Roadblocks, and Landmines)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Document conditions that negatively impact Reliability, Efficiency, Productivity, or Safety (REPS).
        Upon submission, the entry will be routed for approval and Severity Level verification.
      </Typography>

      {noTeams && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Your organization has no teams yet — add Natural Work Teams in Settings → Organization first.
        </Alert>
      )}

      {submitted && (
        <Alert
          icon={<CheckCircleOutlineIcon />}
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSubmitted(false)}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Submitted{shownConditionId ? ` as ${shownConditionId}` : ''} — status Pending Review.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {lastCondition?.aiAnalysis?.suggestedSeverity
              ? `DREW suggests Severity ${lastCondition.aiAnalysis.suggestedSeverity}. `
              : 'DREW is analyzing severity and pattern matches in the background. '}
            Next: {nextReviewerLabel} reviews it (L1). You can edit or withdraw it under My OE Submissions below until that review opens.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }} useFlexGap>
            <Button size="small" variant="contained" onClick={() => { setSubmitted(false); if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Log another</Button>
            <Button size="small" variant="outlined" onClick={() => { if (typeof document !== 'undefined') document.getElementById('my-oe-submissions')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>View in My OE Submissions</Button>
            <Button size="small" variant="text" onClick={() => navigate('/drew')}>Back to OE Dashboard</Button>
          </Stack>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isEditing && (
        <Alert
          severity="info"
          icon={<EditIcon />}
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={cancelEdit}>
              Cancel
            </Button>
          }
        >
          Editing your submission. Make your changes and click <strong>Update submission</strong>.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack spacing={3}>
            {/* Step 1 — What */}
            <Box>
              <StepLabel n={1} text="What did you see?" />
              <Typography variant="overline" color="text.secondary">What type of condition is this?</Typography>
              <ToggleButtonGroup
                value={kind}
                exclusive
                onChange={(_, v) => {
                  if (!v) return;
                  setKind(v);
                  setType(v === 'drift' ? 'drift' : '');
                }}
                sx={{ mt: 1, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, '& .MuiToggleButtonGroup-grouped': { m: 0, border: 1, borderColor: 'divider', borderRadius: '8px !important' } }}
              >
                {CONDITION_KINDS.map((k) => (
                  <ToggleButton key={k.value} value={k.value} sx={{ flexDirection: 'column', py: 1.5, px: 1, gap: 0.5, height: '100%' }}>
                    {k.value === 'drift' ? typeIcon('drift') : <WarningAmberIcon />}
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'none', whiteSpace: 'normal' }}>{k.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'none', whiteSpace: 'normal', display: { xs: 'none', sm: 'block' } }}>{k.description}</Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {/* Tim, Rewording Item 05: Accumulation → "Type of Setup *" (Trap / Roadblock / Landmine) */}
              {kind === 'trl' && (
                <TextField
                  select
                  required
                  fullWidth
                  label="Type of Setup"
                  value={type}
                  onChange={(e) => { setType(e.target.value); if (e.target.value !== 'trap') setTrapCategory(''); }}
                  helperText={TRL_TYPES.find((t) => t.value === type)?.description || 'Which kind of setup for human error is this?'}
                  sx={{ mt: 2 }}
                >
                  {TRL_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </TextField>
              )}
              {/* Subtype — in addition to the free text (C0820: reinforces the training) */}
              {type === 'drift' && (
                <TextField
                  select
                  required
                  fullWidth
                  label="Type of drift"
                  value={driftType}
                  onChange={(e) => setDriftType(e.target.value)}
                  helperText={driftType === 'other' ? 'Describe the drift in the description below.' : 'Which kind of drift did you observe?'}
                  sx={{ mt: 2 }}
                >
                  {DRIFT_TYPES.map((d) => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </TextField>
              )}
              {type === 'trap' && (
                <TextField
                  select
                  required
                  fullWidth
                  label="Trap category"
                  value={trapCategory}
                  onChange={(e) => setTrapCategory(e.target.value)}
                  helperText="One of the eleven predominant traps"
                  sx={{ mt: 2 }}
                >
                  {TRAP_CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </TextField>
              )}
            </Box>

            <TextField
              label="Title"
              required
              placeholder="Short, specific summary (e.g., 'Tool crib closes during turnover')"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <TextField
              label="Description"
              required
              placeholder="What did you observe? When? What's the consequence if it isn't addressed?"
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <Divider />

            {/* Step 2 — Where (prefilled from the member's team when known) */}
            <StepLabel n={2} text="Where?" />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {nwtLocked && personaNwt ? (
                  <TextField
                    fullWidth
                    label="My Natural Work Team (NWT)"
                    value={teamOptions.find((n) => n.id === nwtId)?.name || nwtId}
                    InputProps={{ readOnly: true }}
                    helperText={(
                      <>
                        Auto-populated from your team membership.{' '}
                        <Link component="button" type="button" onClick={() => setNwtLocked(false)} sx={{ verticalAlign: 'baseline' }}>
                          Log for another team
                        </Link>
                      </>
                    )}
                  />
                ) : (
                  <TextField
                    select
                    fullWidth
                    label="Natural Work Team (NWT)"
                    value={nwtId}
                    onChange={(e) => setNwtId(e.target.value)}
                    helperText={personaNwt && nwtId !== personaNwt ? 'Logging for a team other than your own' : 'The team where the condition was observed'}
                  >
                    {teamOptions.map((n) => (
                      <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {!showOwner && !ownerKey ? (
                  <Link component="button" type="button" onClick={() => setShowOwner(true)} sx={{ mt: { md: 2 }, display: 'inline-block' }}>
                    Recommend an owner (optional)
                  </Link>
                ) : (
                <TextField
                  select
                  fullWidth
                  label="Recommended owner of this OE condition"
                  value={ownerKey}
                  onChange={(e) => setOwnerKey(e.target.value)}
                  helperText="Optional — the department, NWT, Site/Plant Manager or Executive best placed to resolve it; reviewers can change it"
                >
                  <MenuItem value="">
                    <em>Not sure — let the reviewers decide</em>
                  </MenuItem>
                  {ownerOptions.map((o) => (
                    <MenuItem key={o.key} value={o.key} sx={{ pl: o.nwtId ? 4 : 2, fontWeight: o.ownerUid ? 600 : 400 }}>{o.label}</MenuItem>
                  ))}
                </TextField>
                )}
              </Grid>
            </Grid>

            <Divider />

            {/* Step 3 — How bad */}
            <Box>
              <StepLabel n={3} text="How bad is it?" />
              <Typography variant="overline" color="text.secondary">REPS quadrants impacted (check all that apply)</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {REPS_QUADRANTS.map((q) => (
                  <FormControlLabel
                    key={q.value}
                    control={(
                      <Checkbox
                        size="small"
                        checked={quadrants.includes(q.value)}
                        onChange={() => toggleQuad(q.value)}
                        sx={{ color: REPS_COLORS[q.value], '&.Mui-checked': { color: REPS_COLORS[q.value] } }}
                      />
                    )}
                    label={<Typography variant="body2" sx={{ color: REPS_COLORS[q.value], fontWeight: 600 }}>{q.label}</Typography>}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">My Severity Assessment</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Choose the Severity Level you believe best fits the condition. Severity levels will be reviewed and approved before the Condition becomes active within DREW.
              </Typography>
              <Stack spacing={1}>
                {SEVERITY_LEVELS.map((s) => (
                  <Box
                    key={s.value}
                    onClick={() => setSeverity(s.value)}
                    sx={{
                      p: 1.5,
                      border: 2,
                      borderColor: severity === s.value ? s.color : 'divider',
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: severity === s.value ? `${s.color}11` : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      transition: 'all 0.15s',
                    }}
                  >
                    <Chip
                      label={s.value}
                      size="small"
                      sx={{ bgcolor: s.color, color: '#fff', fontWeight: 700, minWidth: 36 }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={severity === s.value ? 700 : 500}>{s.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                Submitting as: <strong>{personaUser?.name || 'Demo user'}</strong>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {missingRequired && !submitting && (
                  <Typography variant="caption" color="text.secondary">
                    {noTeams
                      ? 'Add Natural Work Teams in Settings → Organization before logging OE Conditions'
                      : 'Add a title, description, and at least one quadrant'}
                  </Typography>
                )}
                {isEditing && (
                  <Button variant="text" color="inherit" onClick={cancelEdit} disabled={submitting}>
                    Cancel
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                  onClick={handleSubmit}
                  disabled={missingRequired || submitting}
                >
                  {submitting
                    ? 'Saving…'
                    : isEditing
                      ? 'Update submission'
                      : 'Submit for Review'}
                </Button>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Your recent submissions — persistent proof of what was logged. */}
      {mySubmissions.length > 0 && (
        <Paper id="my-oe-submissions" variant="outlined" sx={{ mt: 3, p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            My OE Submissions
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            OE conditions you submitted remain visible in this window so you can see what
            you&rsquo;ve logged. DREW&rsquo;s suggested severity appears automatically once analysis
            finishes. Use <strong>Edit/Review</strong> to edit an entry while it is Pending Review (and to
            review it afterwards), <strong>Resolved</strong> to record that the condition has been resolved,{' '}
            <strong>Withdraw</strong> to remove an entry before it enters review, or <strong>Delete</strong> to
            clear a closed item from your list (the record is kept).
          </Typography>
          <Stack spacing={1.5}>
            {mySubmissions.map((t) => {
              const itm = sevMetaOf(t.severityITM);
              const ai = t.aiAnalysis?.suggestedSeverity
                ? sevMetaOf(t.aiAnalysis.suggestedSeverity)
                : null;
              const statusMeta = statusMetaOf(t.status);
              const editable = t.status === 'submitted';
              const isRowEditing = editingId === t.id;
              return (
                <Box
                  key={t.id}
                  sx={{
                    border: 1,
                    borderColor: isRowEditing ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 1.75,
                    bgcolor: isRowEditing ? 'action.selected' : 'background.paper',
                  }}
                >
                  {/* Row 1: title + status — the two things you scan for. */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, lineHeight: 1.3 }}>
                      {t.conditionId && (
                        <Typography component="span" variant="caption" sx={{ fontFamily: '"Roboto Mono", monospace', mr: 1, color: 'text.secondary' }}>
                          {t.conditionId}
                        </Typography>
                      )}
                      {t.title || '(untitled)'}
                    </Typography>
                    <Chip
                      label={resolvedSubtitle(t) ? `${statusMeta.label} · ${resolvedSubtitle(t)}` : statusMeta.label}
                      size="small"
                      color={statusMeta.color}
                      sx={{ fontWeight: 700, flexShrink: 0 }}
                    />
                  </Box>

                  {/* Row 2: metadata line. */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                    {typeLabelOf(t.type)}
                    {conditionSubtypeLabel(t) ? ` (${conditionSubtypeLabel(t)})` : ''}
                    {t.repsQuadrant ? ` · ${t.repsQuadrant}` : ''}
                    {' · '}
                    {t.submittedAt ? formatTimestamp(t.submittedAt) : 'just now'}
                    {t.status === 'resolved' && t.resolvedByName ? ` · Fixed by ${t.resolvedByName}` : ''}
                  </Typography>

                  {/* Row 3: severity comparison + actions. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 0.25 }}>
                      Severity
                    </Typography>
                    {itm && (
                      <Chip
                        label={`You: ${t.severityITM}`}
                        size="small"
                        sx={{ bgcolor: itm.color, color: '#fff', fontWeight: 700 }}
                      />
                    )}
                    {ai ? (
                      <Chip
                        icon={<AutoAwesomeIcon sx={{ color: '#fff !important', fontSize: 14 }} />}
                        label={`AI suggests: ${t.aiAnalysis.suggestedSeverity}`}
                        size="small"
                        sx={{ bgcolor: ai.color, color: '#fff', fontWeight: 700 }}
                      />
                    ) : (
                      <Chip
                        icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                        label="AI analyzing…"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    {/* Edit/Review (P2-08): editable only while Pending Review; a read-only review view otherwise */}
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={() => (editable ? startEdit(t) : setReviewTarget(t))}
                      disabled={isEditing}
                    >
                      Edit/Review
                    </Button>
                    {canHide(t) && (
                      <Button size="small" variant="text" color="inherit" startIcon={<DeleteOutlineIcon fontSize="small" />} disabled={isEditing || rowBusy} onClick={() => hideConditionFor(t.id, viewer.uid)}>
                        Delete
                      </Button>
                    )}
                    {t.status === 'resolved' ? (
                      (viewer.isAdmin || viewer.role === 'department_lead') && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ReplayIcon fontSize="small" />}
                          onClick={() => handleReopen(t)}
                          disabled={isEditing}
                        >
                          Reopen
                        </Button>
                      )
                    ) : (
                      <>
                        {editable && (
                          <>
                            <Button
                              size="small"
                              variant="text"
                              color="error"
                              startIcon={<DeleteOutlineIcon fontSize="small" />}
                              onClick={() => setDeleteTarget(t)}
                              disabled={isEditing}
                            >
                              Withdraw
                            </Button>
                          </>
                        )}
                        {canResolve(t, viewer) && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<TaskAltIcon fontSize="small" />}
                            onClick={() => { setFixNote(''); setFixTarget(t); }}
                            disabled={isEditing}
                          >
                            Resolved
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Always-visible confirmation, regardless of scroll position. */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          icon={<CheckCircleOutlineIcon />}
          onClose={() => setToastOpen(false)}
          sx={{ width: '100%' }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>

      {/* Withdraw confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !rowBusy && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Withdraw this OE Condition?
          <IconButton size="small" onClick={() => setDeleteTarget(null)} disabled={rowBusy}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove &ldquo;{deleteTarget?.title || 'this submission'}&rdquo;.{' '}
            <strong>Note:</strong> You can only withdraw entries BEFORE they enter the review process.{' '}
            <strong>Use caution:</strong> withdrawing an entry cannot be reversed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={rowBusy}>
            Keep it
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleWithdraw}
            disabled={rowBusy}
            startIcon={rowBusy ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          >
            {rowBusy ? 'Withdrawing…' : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark-as-fixed dialog */}
      <Dialog open={Boolean(fixTarget)} onClose={() => !rowBusy && setFixTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Mark as Resolved
          <IconButton size="small" onClick={() => setFixTarget(null)} disabled={rowBusy}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This action will close &ldquo;{fixTarget?.title || 'this item'}&rdquo;, indicating that actions have been completed to reverse the drift, eliminate the TRL, or to Fortify associated defense(s) such that the condition no longer exists or has been minimized as much as possible.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Resolution notes — what was done?"
            required
            value={fixNote}
            onChange={(e) => setFixNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFixTarget(null)} disabled={rowBusy}>Cancel</Button>
          <Button
            color="success"
            variant="contained"
            onClick={handleResolve}
            disabled={rowBusy || !fixNote.trim()}
            startIcon={rowBusy ? <CircularProgress size={16} color="inherit" /> : <TaskAltIcon />}
          >
            {rowBusy ? 'Saving…' : 'Submit Resolution'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConditionDetailDrawer condition={reviewTarget} viewer={viewer} open={Boolean(reviewTarget)} onClose={() => setReviewTarget(null)} />
    </Box>
  );
}

export default TRLEntry;
